import { DocumentItem as ApiDocumentItem } from 'com.trumbull.phx.document.api-client';
import {
  RecordRetrievalObjectiveItemCreation as ApiObjectiveCreation,
  RecordRetrievalRequestItemCreation as ApiRequestCreation,
} from 'com.trumbull.phx.recordretrievalobjective.api-client';
import { Attachment } from '../order-info/order-info.model';

export interface DocumentItem extends ApiDocumentItem {
  storageId: string;
  sizeText?: string;
  tagNames?: string[];
}

export type DocumentBucket = 'order' | 'request' | 'incoming' | 'outgoing';

export interface DocumentListEntry {
  attachTo?: DocumentBucket;
  documents?: DocumentItem[];
  documentListId?: string;
  documentListProviderId?: string;
}

export interface RecordRetrievalRequestItemCreation extends Omit<ApiRequestCreation, 'documentLists'> {
  documentLists: DocumentListEntry[];
}

export interface RecordRetrievalObjectiveItemCreation extends Omit<ApiObjectiveCreation, 'documentLists' | 'requests'> {
  documentLists: DocumentListEntry[];
  requests?: RecordRetrievalRequestItemCreation[];
}

export type DocumentScope = 'order' | {
  request: { requestUuid?: string; requestNumber?: string };
};

export class DocumentListValidationError extends Error {}

export function buildDocumentLists(
  uploads: readonly Attachment[] | null | undefined,
  scope: DocumentScope,
): DocumentListEntry[] {
  const buckets = new Map<DocumentBucket, DocumentItem[]>();
  for (const upload of uploads ?? []) {
    const target = upload.attachTo?.toLowerCase();
    let bucket: DocumentBucket;
    if (scope === 'order') {
      if (target !== undefined && target !== 'order') {
        throw new DocumentListValidationError(`Unsupported order document target: ${upload.attachTo}`);
      }
      bucket = 'order';
    } else {
      const legacyPrefix = scope.request.requestNumber?.toLowerCase();
      const legacyTargets: Record<string, DocumentBucket> = legacyPrefix ? {
        [`${legacyPrefix}-request`]: 'request',
        [`${legacyPrefix}-in`]: 'incoming',
        [`${legacyPrefix}-out`]: 'outgoing',
      } : {};
      const normalized = target === undefined ? 'request' : legacyTargets[target] ?? target;
      if (normalized !== 'request' && normalized !== 'incoming' && normalized !== 'outgoing') {
        throw new DocumentListValidationError(`Unsupported request document target: ${upload.attachTo}`);
      }
      bucket = normalized;
    }
    if (typeof upload.storageId !== 'string' || !upload.storageId.trim()) {
      throw new DocumentListValidationError(`Document ${upload.filename} has no storageId.`);
    }
    const documents = buckets.get(bucket) ?? [];
    documents.push({
      attributes: upload.attributes,
      contentType: upload.type,
      description: upload.description ?? upload.filename,
      fileName: upload.filename,
      displayName: upload.filename,
      sizeBytes: upload.size,
      sizeText: upload.sizeText,
      storageId: upload.storageId,
      storageProviderId: upload.storageProviderId,
      externalKey: upload.filename,
      tagNames: [upload.label],
    });
    buckets.set(bucket, documents);
  }
  return Array.from(buckets, ([attachTo, documents]) => ({ attachTo, documents }));
}